import { CurrencyPipe, NgClass, NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SfProductLinkDirective } from '@scalefast/components';
import { StoreConfigService, StoreSpecificConfigService } from '@scalefast/config-angular';
import { CartProduct, Product } from '@scalefast/core';
import { Subject, debounceTime } from 'rxjs';
import { MISSING_PRODUCT_IMAGE_URL } from 'src/app/app.tokens';
import { GenericEventService } from 'src/app/shared/services';
import { TranslateProductFieldPipe } from 'src/app/shared/pipes/translate-product-field.pipe';

@Component({
  selector: 'app-minicart-item',
  templateUrl: './minicart-item.component.html',
  styleUrls: ['./minicart-item.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgOptimizedImage,
    RouterLink,
    SfProductLinkDirective,
    NgClass,
    CurrencyPipe,
    TranslateProductFieldPipe,
  ],
})
export class AppMinicartItemComponent implements OnInit, OnChanges {
  @Input() product: CartProduct = {} as CartProduct;
  @Input() productsTillCartFull = 0;
  @Output() updateCart = new EventEmitter<CartProduct>();
  @Output() removeFromCart = new EventEmitter<CartProduct>();
  private destroyRef = inject(DestroyRef);
  maxProductValue: number;
  quantityArray: Array<number> = [];
  updateCartForm: FormGroup = {} as FormGroup;

  oldPrice: number | undefined;
  offerFeed: string | undefined;
  isOfferFeed: boolean = true;
  hideCrossedPrice: boolean = false;
  feedId: string | string[] | undefined;
  bundleCrossedPriceIsRecommendedPrice: boolean;
  bundleOldPrice: number | undefined;

  currentProductsTillCartFull = 0;
  // TODO: Are we using this subject only for the debounceTime? If so, we could create our debounce method
  private itemQuantityChange: Subject<number> = new Subject();

  constructor(
    private genericEventService: GenericEventService,
    private storeConfigService: StoreConfigService,
    private storeSpecificConfigService: StoreSpecificConfigService,
    @Inject(MISSING_PRODUCT_IMAGE_URL) public missingProductImageUrl: string,
  ) {
    let specificConfig = this.storeSpecificConfigService.get();
    this.maxProductValue = storeConfigService.get().maxItemQuantity;
    this.hideCrossedPrice = specificConfig.hideCrossedPrice;
    this.offerFeed = specificConfig.offerFeed;
    this.bundleCrossedPriceIsRecommendedPrice = specificConfig.bundleCrossedPriceIsRecommendedPrice;
  }

  ngOnInit() {
    this.createForm();
    this.itemQuantityChange
      .asObservable()
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((qtyChanged) => {
        this.changeCart(qtyChanged);
      });
    this.update();
  }

  createForm() {
    const maxUnits = this.product.stock || this.maxProductValue;
    this.quantityArray = Array.from({ length: maxUnits }, (_, i) => i + 1);

    this.updateCartForm = new FormGroup({
      stock: new FormControl(maxUnits),
      quantity: new FormControl(
        this.product.quantity,
        Validators.compose([Validators.required, Validators.min(1), Validators.max(maxUnits)]),
      ),
      productId: new FormControl(this.product.productId, Validators.required),
      price: new FormControl(this.product.price),
    });
  }

  ngOnChanges(ch: SimpleChanges) {
    if (ch?.['product']?.previousValue?.quantity !== ch?.['product']?.currentValue?.quantity) {
      this.createForm();
    }
    this.currentProductsTillCartFull = this.productsTillCartFull;
    this.update();
  }

  incrementQuantity() {
    this.updateCartForm.value.quantity += 1;
    this.product.quantity += 1;
    this.currentProductsTillCartFull--;

    this.itemQuantityChange.next(1);
  }
  decrementQuantity(productQuantity: number) {
    this.updateCartForm.value.quantity = productQuantity - 1;
    this.product.quantity -= 1;
    this.currentProductsTillCartFull++;
    this.itemQuantityChange.next(-1);
  }

  changeCart(qtyChanged: number) {
    this.product.quantityChanged = qtyChanged;
    const productData = { ...this.product, ...this.updateCartForm.value };
    this.updateCart.emit(productData);
  }

  updatePrice(price: number, quantity: number) {
    return parseFloat((price * quantity).toFixed(2));
  }

  onSubmit() {
    // console.log(this.updateCartForm.value);
  }

  addProductToWhishlist() {
    console.log('addProductToWhishlist');
  }

  removeProductFromMinicart() {
    this.removeFromCart.emit(this.product);
  }

  get currency(): string | undefined {
    return this.product.currency;
  }

  get quantity() {
    return this.updateCartForm.get('quantity');
  }

  get productPrice() {
    return this.product.quantity * this.product.price;
  }

  get oldProductPrice() {
    this.oldPrice = this.product.oldPrice;
    this.product.product.price.currencies.find((price) => {
      if (price.currency === this.currency) {
        this.oldPrice = price.prices[0].oldPrice;
      } else {
        this.oldPrice = 0;
      }
    });
    if (this.bundleOldPrice) {
      return this.product.quantity * this.bundleOldPrice!;
    }
    return this.product.quantity * this.oldPrice!;
  }

  quantityTracker = (unit: number) => unit;

  clickProductLink(): void {
    this.genericEventService.clickProductLink();
  }

  private update() {
    // FIXME: this looks very fragile
    this.feedId = this.product?.product?.v4ApiResponse?.feedID;
    if (this.hideCrossedPrice && this.offerFeed) {
      this.isOfferFeed = this.feedId?.includes(this.offerFeed) ?? false;
    }
    if (this.bundleCrossedPriceIsRecommendedPrice) {
      let bundleRecommendedPrices;
      const bundleRecommendedPricesAPIv4 =
        this.product.product.v4ApiResponse?.specific?.bundle_info?.bundle_recommended_prices?.elements;
      const bundleRecommendedPricesStoreSearch = this.product.product.v4ApiResponse?.specific?.elements;
      if (bundleRecommendedPricesAPIv4) {
        bundleRecommendedPrices = bundleRecommendedPricesAPIv4.map((priceInfo: any) => {
          const quantity = priceInfo.quantity_in_bundle ?? 1;
          if (!priceInfo?.prices) {
            return;
          }
          const firstCurrencyInfo: any = Object.values(priceInfo.prices)[0];
          return firstCurrencyInfo?.prices?.[0]?.recommended_price * quantity;
        });
      }
      if (bundleRecommendedPricesStoreSearch) {
        bundleRecommendedPrices = bundleRecommendedPricesStoreSearch.map((element: any) => {
          const quantity = element.quantity_in_bundle ?? element.quantity ?? 1;
          if (!element.price_info?.currencies) {
            return;
          }
          const firstCurrencyInfo: any = Object.values(element.price_info.currencies)[0];
          if (firstCurrencyInfo.prices) {
            return firstCurrencyInfo?.prices[0]?.recommended_price * quantity;
          }
          return firstCurrencyInfo?.[0]?.recommended_price * quantity;
        });
      }

      const recommendedPriceSum = bundleRecommendedPrices?.reduce((acc: number, price: string) => {
        return acc + (price ? parseFloat(price) : 0);
      }, 0);
      this.bundleOldPrice = recommendedPriceSum;
    }
    if (this.hideCrossedPrice && this.offerFeed) {
      this.isOfferFeed = this.feedId?.includes(this.offerFeed) ?? false;
    }
  }

  shouldShowCrossedPrice(): boolean {
    return (
      this.oldProductPrice > 0 &&
      this.oldProductPrice !== this.productPrice &&
      (!this.hideCrossedPrice || (this.hideCrossedPrice && this.isOfferFeed))
    );
  }
}
